import { app } from "../config/firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";

const CACHE_TTL_MS = 5 * 60 * 1000;
const inMemoryCache = new Map();

const normalizeKeys = (keys) => {
  if (!keys) return [];
  const list = Array.isArray(keys) ? keys : [keys];
  return Array.from(
    new Set(
      list
        .map((key) => (typeof key === "string" ? key.trim() : ""))
        .filter(Boolean)
    )
  );
};

const buildCacheKey = (path, keys) => {
  const normalizedKeys = normalizeKeys(keys).sort();
  return `${path}::${normalizedKeys.join(",")}`;
};

const readCache = (cacheKey) => {
  const now = Date.now();
  const mem = inMemoryCache.get(cacheKey);
  if (mem && mem.expiresAt > now) return mem.value;
  if (mem) inMemoryCache.delete(cacheKey);

  try {
    const raw = sessionStorage.getItem(`eia_signed_videos:${cacheKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || parsed.expiresAt <= now) {
      sessionStorage.removeItem(`eia_signed_videos:${cacheKey}`);
      return null;
    }
    inMemoryCache.set(cacheKey, parsed);
    return parsed.value || null;
  } catch {
    return null;
  }
};

const writeCache = (cacheKey, value) => {
  const payload = {
    value,
    expiresAt: Date.now() + CACHE_TTL_MS,
  };
  inMemoryCache.set(cacheKey, payload);
  try {
    sessionStorage.setItem(`eia_signed_videos:${cacheKey}`, JSON.stringify(payload));
  } catch {
    // ignora limite de storage
  }
};

const fetchSignedVideosForPath = async (path, keys = []) => {
  const normalizedKeys = normalizeKeys(keys);
  const cacheKey = buildCacheKey(path, normalizedKeys);
  const cached = readCache(cacheKey);
  if (cached) return cached;

  const functions = getFunctions(app, "southamerica-east1");
  const callable = httpsCallable(functions, "getSignedVideoUrls");
  const { data } = await callable({ path, keys: normalizedKeys });
  const urls = data?.urls || {};
  writeCache(cacheKey, urls);
  return urls;
};

const normalizePaths = (paths) => {
  if (!paths) return [];
  const list = Array.isArray(paths) ? paths : [paths];
  return list.filter((path) => typeof path === "string" && path.trim());
};

const applyAliases = (map, aliases = {}) => {
  const out = { ...(map || {}) };
  Object.entries(aliases || {}).forEach(([alias, targetKey]) => {
    if (!out[alias] && out[targetKey]) {
      out[alias] = out[targetKey];
    }
  });
  return out;
};

const hasAllRequiredKeys = (map, requiredKeys = []) => {
  if (!requiredKeys.length) return false;
  return requiredKeys.every((key) => !!map[key]);
};

/**
 * Recupera URLs assinadas de leitura para vídeos (.mp4) dentro de um ou mais caminhos.
 * O caminho deve ser relativo ao bucket, ex: "eia/media/wwcalc".
 * - Quando múltiplos caminhos são informados, o primeiro tem prioridade (override).
 * - "aliases" permite criar chaves alternativas para vídeos existentes.
 * - "keys" limita a busca às chaves necessárias do fluxo atual.
 * - Em fallback, erro de um caminho não interrompe os demais.
 */
export async function fetchSignedVideos(paths, aliases = {}, options = {}) {
  const pathList = normalizePaths(paths);
  if (!pathList.length) return {};

  const requiredKeys = normalizeKeys(options?.keys);
  let merged = {};

  for (const path of pathList) {
    try {
      const map = await fetchSignedVideosForPath(path, requiredKeys);
      for (const [key, url] of Object.entries(map || {})) {
        if (!merged[key]) merged[key] = url;
      }
      merged = applyAliases(merged, aliases);

      if (hasAllRequiredKeys(merged, requiredKeys)) {
        break;
      }
    } catch (error) {
      // Mantém fallback ativo mesmo quando um caminho específico falha.
      console.warn("[EIA] Falha ao carregar videos para path:", path, error?.message || error);
    }
  }

  return applyAliases(merged, aliases);
}
